import { VerlengingStatus, VestigingBeschikbareFaciliteiten, VestigingStatus } from "@/entities/vestiging";
import { AangevraagdeVestiging, AangevraagdeVestigingOpeningstijd } from "./aangevraagde-vestiging";
import { BeheerVestiging } from "./beheer-vestiging";

export type Vestiging = {
    beheerVestiging: BeheerVestiging;

    aangevraagdeVestiging?: AangevraagdeVestiging; // aangevraagdeVestiging is optional because it will not be available on nieuwe-aanvraag-regievoerder for records for which there is no aanvraag yet. In all other cases, it should be available
    currentOpeningstijden: AangevraagdeVestigingOpeningstijd; // usually today's opening hours, but if a dossier spans multiple days and the aanvraag is for a previous day we store the opening hours of the aanvraag day here. This way, we keep the history for previous requests and avoid edge case problems when extending the opening hours for a vestiging after 00:00 AM.,
    isSelectableForSelectAll?: boolean;
};

export const Vestiging = {
    groupByStation: (vestigingen: Vestiging[]) =>
        Object.groupBy(vestigingen, (v) => v.beheerVestiging.stationCode) as Record<string, Vestiging[]>,

    getDisplayName(vestiging: Vestiging) {
        // Assume that if there are only uppercase letters before a dash, it represents the stationCode that we want to strip. Actually fetching the stations to do a proper check gave some performance issues with the table rows, and this approach should work in practice
        // UT - Kiosk - Stationshal --> Kiosk - Stationshal
        // UT - SANIFAIR Toilet - Stationshal --> SANIFAIR Toilet - Stationshal
        const stripStationPrefix = (name: string) => {
            if (!name.includes("-")) return name;

            const [prefix, ...rest] = name.split("-");

            if (prefix && /^\s*[A-Z]+\s*$/.test(prefix)) return rest.join("-").trimStart();

            return name;
        };
        return stripStationPrefix(vestiging.beheerVestiging.vestigingNaam);
    },

    sort: (vestigingen: Vestiging[]) => {
        const collator = new Intl.Collator("nl", { sensitivity: "base", numeric: true });
        return [...vestigingen].sort((a, b) => {
            // VoorkeursVestiging first
            if (a.beheerVestiging.isNsRetailVestiging && !b.beheerVestiging.isNsRetailVestiging) return -1;
            if (!a.beheerVestiging.isNsRetailVestiging && b.beheerVestiging.isNsRetailVestiging) return 1;

            // Only-toilet last
            const onlyToilet = (v: Vestiging) =>
                v.beheerVestiging.beschikbareFaciliteiten.length === 1 &&
                v.beheerVestiging.beschikbareFaciliteiten[0] === VestigingBeschikbareFaciliteiten.TOILET;
            if (onlyToilet(a) && !onlyToilet(b)) return 1;
            if (!onlyToilet(a) && onlyToilet(b)) return -1;

            // Natural sort by name. This way "Kiosk - Spoor 3" comes before "Kiosk - Spoor 11"
            return collator.compare(a.beheerVestiging.vestigingNaam, b.beheerVestiging.vestigingNaam);
        });
    },

    isKlantenserviceOpenAanvraag: (vestiging: Vestiging) =>
        vestiging.aangevraagdeVestiging?.status === VestigingStatus.BESTELD ||
        vestiging.aangevraagdeVestiging?.status === VestigingStatus.STOP_AANGEVRAAGD ||
        Vestiging.isOpenstaandeVerlenging(vestiging),

    isKlantenserviceBehandeldeAanvraag: (vestiging: Vestiging) =>
        vestiging.aangevraagdeVestiging?.status === VestigingStatus.GESTOPT ||
        vestiging.aangevraagdeVestiging?.status === VestigingStatus.GAAT_NIET_LEVEREN ||
        (vestiging.aangevraagdeVestiging?.status === VestigingStatus.ACTIEF && !Vestiging.isOpenstaandeVerlenging(vestiging)),

    canRequestStart: (vestiging: Vestiging) => {
        return (
            !vestiging.aangevraagdeVestiging?.status ||
            vestiging.aangevraagdeVestiging?.status === VestigingStatus.GAAT_NIET_LEVEREN ||
            vestiging.aangevraagdeVestiging?.status === VestigingStatus.GESTOPT
        );
    },

    canRequestStop(vestiging: Vestiging) {
        return vestiging.aangevraagdeVestiging?.status === VestigingStatus.ACTIEF;
    },

    canRequestBulkStop(vestigingen: Vestiging[]) {
        return vestigingen.some((v) => Vestiging.canRequestStop(v));
    },

    isOpen: (vestiging: Vestiging, d: Date = new Date()): boolean => {
        const [openHour, openMinute] = vestiging.currentOpeningstijden.openVan.split(":").map(Number);
        const [closeHour, closeMinute] = vestiging.currentOpeningstijden.openTot.split(":").map(Number);

        const openTime = new Date(d);
        openTime.setHours(openHour, openMinute, 0, 0);

        const closeTime = new Date(d);
        closeTime.setHours(closeHour, closeMinute, 0, 0);

        // If close is less than or equal to open, it means closing is after midnight
        if (closeTime <= openTime) {
            closeTime.setDate(closeTime.getDate() + 1); // move close to next day
        }

        return d >= openTime && d < closeTime;
    },

    isClosed: (vestiging: Vestiging, d: Date = new Date()): boolean => {
        return !Vestiging.isOpen(vestiging, d);
    },

    isOpenContinuouslyOvernight: (vestiging: Vestiging) => {
        const openTot = vestiging.currentOpeningstijden.openTot;
        const tomorrow = (BeheerVestiging.getDayOfTheWeek() + 1) % 7;
        const openVanTomorrow = vestiging.beheerVestiging.openingstijden.find((ot) => ot.dag === tomorrow)?.openVan;

        return openTot === "24:00" && openVanTomorrow === "00:00";
    },

    getMinutesUntilClose: (vestiging: Vestiging, d: Date = new Date()): number | null => {
        // in rare cases a vestiging does not close. In these cases, we are not interested in the number of minutes until close (return null to avoid avoid showing a "closes soon" badge)
        if (Vestiging.isOpenContinuouslyOvernight(vestiging)) return null;

        const [openHour, openMinute] = vestiging.currentOpeningstijden.openVan.split(":").map(Number);
        const [closeHour, closeMinute] = vestiging.currentOpeningstijden.openTot.split(":").map(Number);

        const openTime = new Date(d);
        openTime.setHours(openHour, openMinute, 0, 0);

        const closeTime = new Date(d);
        closeTime.setHours(closeHour, closeMinute, 0, 0);

        // If close is less than or equal to open, it means closing is after midnight
        if (closeTime <= openTime) {
            closeTime.setDate(closeTime.getDate() + 1);
        }

        const now = d.getTime();
        const close = closeTime.getTime();

        const diffMs = close - now;
        if (diffMs <= 0) {
            return null;
        }
        return Math.floor(diffMs / (1000 * 60));
    },

    canModifyOpeningHours(vestiging: Vestiging, variant: "nieuweAanvraag" | "bestaandeAanvraag") {
        const openingstijdenStatus = vestiging.currentOpeningstijden.aangepasteOpenTotStatus;
        if (
            !vestiging.beheerVestiging.isNsRetailVestiging &&
            (openingstijdenStatus === VerlengingStatus.ACTIEF ||
                openingstijdenStatus === VerlengingStatus.AFGEWEZEN ||
                openingstijdenStatus === VerlengingStatus.GESTOPT)
        ) {
            return false;
        }

        switch (variant) {
            case "nieuweAanvraag":
                return (
                    !vestiging.aangevraagdeVestiging?.status ||
                    vestiging.aangevraagdeVestiging?.status === VestigingStatus.GESTOPT ||
                    vestiging.aangevraagdeVestiging?.status === VestigingStatus.GAAT_NIET_LEVEREN
                );
            case "bestaandeAanvraag":
                return (
                    vestiging.aangevraagdeVestiging?.status === VestigingStatus.BESTELD ||
                    vestiging.aangevraagdeVestiging?.status === VestigingStatus.ACTIEF
                );
            default:
                return false;
        }
    },

    isOpenstaandeVerlenging: (vestiging: Vestiging) =>
        !vestiging.beheerVestiging.isNsRetailVestiging &&
        vestiging?.currentOpeningstijden.aangepasteOpenTotStatus === VerlengingStatus.AANGEVRAAGD,

    heeftAangepasteOpeningstijd: (vestiging: Vestiging) =>
        vestiging.currentOpeningstijden.aangepasteOpenTot &&
        (vestiging.currentOpeningstijden.aangepasteOpenTotStatus === VerlengingStatus.ACTIEF ||
            vestiging.currentOpeningstijden.aangepasteOpenTotStatus === VerlengingStatus.GESTOPT),
};
